import { useEffect, useRef } from "react";
import type { FieldPath } from "react-hook-form";

import type { LPQuoteInput } from "../../types/Rater.types";
import {
  getAllowedValuationKeys,
  getDefaultValuationForPeril,
  getFallbackValuationForPeril,
  getValuationOptions,
  isValuationAllowed,
} from "../rules";
import { BusinessRulesArgs } from "./useBusinessRules";

const PROHIBITED_FORMS_BY_PERIL = {
  FL1R: ["FL46A", "FL55", "FL72A", "FL342", "FL345"],
  FL1RV: ["FL46A", "FL55", "FL72A", "FL342", "FL345"],
  FL2: [],
  FL3: [],
} as const;

type Peril = keyof typeof PROHIBITED_FORMS_BY_PERIL;

export function getProhibitedFormsByPeril(perils?: string): readonly string[] {
  return PROHIBITED_FORMS_BY_PERIL[perils as Peril] ?? [];
}

export function isFormProhibitedByPeril(perils: string | undefined, formName: string) {
  return getProhibitedFormsByPeril(perils).includes(formName);
}

export function usePerilRules({ tabItem, locationValues, setValue, trigger, enabled }: BusinessRulesArgs) {
  const { perils, valuation } = locationValues;
  const allowedValuationKeys = getAllowedValuationKeys(perils, enabled);
  const valuationOptions = getValuationOptions(perils, enabled);
  const previousPerilsRef = useRef(perils);

  // {START: Set to default valuation when Peril is changed}
  useEffect(() => {
    const previousPerils = previousPerilsRef.current;
    const perilsChanged = previousPerils !== perils;
    previousPerilsRef.current = perils;

    const defaultValuation = enabled && perilsChanged ? getDefaultValuationForPeril(perils, enabled) : undefined;
    const fallbackValuation = isValuationAllowed(perils, valuation, enabled)
      ? undefined
      : getFallbackValuationForPeril(perils, enabled);
    const nextValuation = defaultValuation ?? fallbackValuation;

    if (!nextValuation) return;
    if (valuation === nextValuation) return;

    const valuationPath = `locations.${tabItem}.valuation` as FieldPath<LPQuoteInput>;

    setValue(valuationPath, nextValuation, { shouldValidate: true, shouldDirty: true, shouldTouch: true });

    void trigger([valuationPath, `locations.${tabItem}.RCcovA`, "liability"] as FieldPath<LPQuoteInput>[]);
  }, [perils, valuation, tabItem, setValue, trigger, enabled]);
  // {END: Set to default valuation when Peril is changed}

  return {
    allowedValuationKeys,
    valuationOptions,
  };
}
